import { StorageService } from './storage';
import { BTCPriceHistoryService } from './btcPriceHistory';
import type { Token } from '../types';
import type { ATHCache } from './athService';

/**
 * Portfolio interface
 */
export interface Portfolio {
  id: string;
  name: string;
  description?: string;
  tokens: Token[];
  createdAt: number;
  updatedAt: number;
  color?: string; // For visual identification
  icon?: string; // Emoji icon
}

/**
 * Portfolio Manager Service
 * Gère plusieurs portfolios avec sélection, création, et consolidation
 */
class PortfolioManagerService {
  private readonly STORAGE_KEY = 'portfolios';
  private readonly ACTIVE_PORTFOLIO_KEY = 'active_portfolio_id';
  private readonly DEFAULT_PORTFOLIO_ID = 'default';

  /**
   * Initialize avec un portfolio par défaut si nécessaire
   */
  initialize(): void {
    const portfolios = this.getAllPortfolios();

    if (portfolios.length === 0) {
      // Créer portfolio par défaut
      const existingTokens = StorageService.get<Token[]>('portfolio_tokens', []);

      const defaultPortfolio: Portfolio = {
        id: this.DEFAULT_PORTFOLIO_ID,
        name: 'Portfolio Principal',
        description: 'Mon portfolio principal',
        tokens: existingTokens,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        color: '#6366f1',
        icon: '💼'
      };

      this.savePortfolio(defaultPortfolio);
      this.setActivePortfolio(this.DEFAULT_PORTFOLIO_ID);
    }
  }

  /**
   * Récupère tous les portfolios
   */
  getAllPortfolios(): Portfolio[] {
    const portfolios = StorageService.get<Portfolio[]>(this.STORAGE_KEY, []);

    // Ensure tokens array exists for each portfolio (fix corrupted data)
    return (portfolios || []).map(p => ({
      ...p,
      tokens: Array.isArray(p?.tokens) ? p.tokens : []
    }));
  }

  /**
   * Récupère un portfolio par ID
   */
  getPortfolio(id: string): Portfolio | null {
    const portfolios = this.getAllPortfolios();
    return portfolios.find(p => p.id === id) || null;
  }

  /**
   * Récupère le portfolio actif
   */
  getActivePortfolio(): Portfolio | null {
    const activeId = this.getActivePortfolioId();
    if (!activeId) return null;

    return this.getPortfolio(activeId);
  }

  /**
   * Récupère l'ID du portfolio actif
   */
  getActivePortfolioId(): string | null {
    return StorageService.get<string>(this.ACTIVE_PORTFOLIO_KEY, this.DEFAULT_PORTFOLIO_ID);
  }

  /**
   * Définit le portfolio actif
   */
  setActivePortfolio(id: string): boolean {
    const portfolio = this.getPortfolio(id);
    if (!portfolio) return false;

    StorageService.set(this.ACTIVE_PORTFOLIO_KEY, id);
    return true;
  }

  /**
   * Crée un nouveau portfolio
   */
  createPortfolio(name: string, description?: string, icon?: string, color?: string): Portfolio {
    const portfolios = this.getAllPortfolios();

    const newPortfolio: Portfolio = {
      id: `portfolio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name,
      description,
      tokens: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      color: color || this.getRandomColor(),
      icon: icon || this.getRandomIcon()
    };

    portfolios.push(newPortfolio);
    StorageService.set(this.STORAGE_KEY, portfolios);

    return newPortfolio;
  }

  /**
   * Sauvegarde un portfolio (création ou mise à jour)
   */
  savePortfolio(portfolio: Portfolio): void {
    const portfolios = this.getAllPortfolios();
    const index = portfolios.findIndex(p => p.id === portfolio.id);

    portfolio.updatedAt = Date.now();

    if (index >= 0) {
      portfolios[index] = portfolio;
    } else {
      portfolios.push(portfolio);
    }

    StorageService.set(this.STORAGE_KEY, portfolios);
  }

  /**
   * Met à jour les tokens d'un portfolio
   */
  updatePortfolioTokens(id: string, tokens: Token[]): boolean {
    const portfolio = this.getPortfolio(id);
    if (!portfolio) return false;

    portfolio.tokens = tokens;
    portfolio.updatedAt = Date.now();
    this.savePortfolio(portfolio);

    return true;
  }

  /**
   * Renomme un portfolio
   */
  renamePortfolio(id: string, name: string, description?: string): boolean {
    const portfolio = this.getPortfolio(id);
    if (!portfolio) return false;

    portfolio.name = name;
    if (description !== undefined) {
      portfolio.description = description;
    }
    portfolio.updatedAt = Date.now();
    this.savePortfolio(portfolio);

    return true;
  }

  /**
   * Supprime un portfolio
   */
  deletePortfolio(id: string): boolean {
    if (id === this.DEFAULT_PORTFOLIO_ID) {
      return false; // Ne pas supprimer le portfolio par défaut
    }

    const portfolios = this.getAllPortfolios();
    const filtered = portfolios.filter(p => p.id !== id);

    if (filtered.length === portfolios.length) {
      return false; // Portfolio not found
    }

    StorageService.set(this.STORAGE_KEY, filtered);

    // Si c'était le portfolio actif, basculer sur le premier disponible
    if (this.getActivePortfolioId() === id) {
      const firstPortfolio = filtered[0];
      if (firstPortfolio) {
        this.setActivePortfolio(firstPortfolio.id);
      }
    }

    return true;
  }

  /**
   * Duplique un portfolio
   */
  duplicatePortfolio(id: string, newName?: string): Portfolio | null {
    const original = this.getPortfolio(id);
    if (!original) return null;

    const duplicate: Portfolio = {
      ...original,
      id: `portfolio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: newName || `${original.name} (Copie)`,
      tokens: JSON.parse(JSON.stringify(original.tokens)), // Deep clone
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    const portfolios = this.getAllPortfolios();
    portfolios.push(duplicate);
    StorageService.set(this.STORAGE_KEY, portfolios);

    return duplicate;
  }

  /**
   * Vue consolidée de tous les portfolios
   */
  getConsolidatedView(): {
    totalValue: number;
    totalCost: number;
    totalPL: number;
    portfoliosCount: number;
    tokensCount: number;
    portfolios: Array<{
      id: string;
      name: string;
      value: number;
      tokensCount: number;
    }>;
  } {
    const portfolios = this.getAllPortfolios();

    return {
      totalValue: 0,
      totalCost: 0,
      totalPL: 0,
      portfoliosCount: portfolios.length,
      tokensCount: portfolios.reduce((sum, p) => sum + p.tokens.length, 0),
      portfolios: portfolios.map(p => ({
        id: p.id,
        name: p.name,
        value: 0, // À calculer avec les prix actuels
        tokensCount: p.tokens.length
      }))
    };
  }

  /**
   * Couleurs aléatoires pour les portfolios
   */
  private getRandomColor(): string {
    const colors = [
      '#6366f1', // indigo
      '#8b5cf6', // violet
      '#ec4899', // pink
      '#f59e0b', // amber
      '#10b981', // emerald
      '#06b6d4', // cyan
      '#3b82f6', // blue
      '#ef4444'  // red
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  /**
   * Icônes aléatoires pour les portfolios
   */
  private getRandomIcon(): string {
    const icons = ['💼', '📊', '💰', '🚀', '💎', '📈', '🎯', '⭐', '🔥', '💵'];
    return icons[Math.floor(Math.random() * icons.length)];
  }

  /**
   * Exporte un portfolio en JSON avec TOUTES les données associées
   * @param includeAllCaches - Inclure tous les caches (BTC, ATH, MA200)
   * @param includeAlerts - Inclure les alertes de prix
   * @returns Promise<string | null> - JSON string or null if portfolio not found
   *
   * NOTE: This is now async to ensure BTC historical prices are loaded for all buy positions
   */
  async exportPortfolio(id: string, includeAllCaches = true, includeAlerts = true): Promise<string | null> {
    const portfolio = this.getPortfolio(id);
    if (!portfolio) return null;

    const exportData: any = {
      version: '2.0', // Incremented version for new format
      portfolio,
      timestamp: Date.now(),
      exportDate: new Date().toISOString()
    };

    if (includeAllCaches) {
      // BTC Price History Cache - ENHANCED to ensure all buy dates are included
      // This guarantees "% vs BTC" will work immediately after import without API calls
      const btcCache = StorageService.get('btc_price_history_cache', { prices: {}, lastUpdated: Date.now() });

      // Extract all buy position dates from the portfolio
      const buyDates = new Set<string>();
      portfolio.tokens.forEach(token => {
        token.positions.forEach(position => {
          if (position.type === 'buy') {
            buyDates.add(position.date);
          }
        });
      });

      // Load BTC prices for all buy dates (will use cache when available).
      // BTCPriceHistoryService is imported statically at the top — no
      // circular dependency from btcPriceHistory back to portfolioManager.
      if (buyDates.size > 0) {
        const buyDatesArray = Array.from(buyDates);

        console.log(`📊 Ensuring BTC prices for ${buyDatesArray.length} buy dates before export...`);
        const historicalPrices = await BTCPriceHistoryService.getBTCPricesForDates(buyDatesArray);

        // Merge with existing cache
        const mergedPrices = { ...btcCache.prices, ...historicalPrices };

        // Update the cache structure
        exportData.btcHistoricalPrices = {
          prices: mergedPrices,
          lastUpdated: Date.now()
        };

        console.log(`✅ Exporting ${Object.keys(mergedPrices).length} BTC historical prices`);
      } else {
        exportData.btcHistoricalPrices = btcCache;
      }

      // ATH/Min2Y Cache (consolidated storage)
      exportData.athCache = StorageService.get('wealthhub_ath_cache', {});

      // MA200 Cache - Export all token price history caches
      const ma200Cache: Record<string, any> = {};
      portfolio.tokens.forEach(token => {
        // Try common durations: 200d, 365d, 730d
        [200, 365, 730].forEach(days => {
          const cacheKey = `token_price_history_${token.id}_${days}d`;
          const cached = StorageService.get(cacheKey, null);
          if (cached) {
            ma200Cache[cacheKey] = cached;
          }
        });
      });
      exportData.ma200Cache = ma200Cache;
    }

    if (includeAlerts) {
      // Price Alerts
      const allAlerts = StorageService.get('price_alerts', []);
      // Filter alerts for tokens in this portfolio
      const portfolioTokenIds = portfolio.tokens.map(t => t.id);
      const portfolioAlerts = allAlerts.filter((alert: any) =>
        portfolioTokenIds.includes(alert.tokenId)
      );
      exportData.priceAlerts = portfolioAlerts;
    }

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Importe un portfolio depuis JSON avec toutes les données associées
   * Compatible avec version 1.0 et 2.0
   */
  importPortfolio(jsonData: string): Portfolio | null {
    try {
      const data = JSON.parse(jsonData);

      if (!data.portfolio || !data.portfolio.tokens) {
        throw new Error('Invalid portfolio data');
      }

      const version = data.version || '1.0';
      console.log(`📥 Importing portfolio (version ${version})`);

      // Générer un nouvel ID pour éviter les conflits
      const importedPortfolio: Portfolio = {
        ...data.portfolio,
        id: `portfolio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: `${data.portfolio.name} (Importé)`,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      // Save portfolio
      this.savePortfolio(importedPortfolio);
      console.log(`✅ Portfolio imported: ${importedPortfolio.tokens.length} tokens`);

      // Import BTC Historical Prices Cache (v1.0 & v2.0)
      if (data.btcHistoricalPrices) {
        StorageService.set('btc_price_history_cache', data.btcHistoricalPrices);
        // Handle both old format (direct prices object) and new format (with wrapper)
        const pricesCount = data.btcHistoricalPrices.prices
          ? Object.keys(data.btcHistoricalPrices.prices).length
          : Object.keys(data.btcHistoricalPrices).length;
        console.log(`✅ BTC cache imported: ${pricesCount} historical prices`);
      }

      // Import ATH Cache (v1.0 & v2.0)
      if (data.athCache) {
        StorageService.set('wealthhub_ath_cache', data.athCache);
        const count = Object.keys(data.athCache).length;
        console.log(`✅ ATH/Min2Y cache imported: ${count} tokens`);
      }

      // Import MA200 Cache (v2.0 only)
      if (version === '2.0' && data.ma200Cache) {
        const cacheKeys = Object.keys(data.ma200Cache);
        cacheKeys.forEach(key => {
          StorageService.set(key, data.ma200Cache[key]);
        });
        console.log(`✅ MA200 cache imported: ${cacheKeys.length} entries`);
      }

      // Import Price Alerts (v2.0 only)
      if (version === '2.0' && data.priceAlerts && Array.isArray(data.priceAlerts)) {
        const existingAlerts = StorageService.get('price_alerts', []);
        // Merge alerts, avoiding duplicates
        const mergedAlerts = [...existingAlerts];

        data.priceAlerts.forEach((alert: any) => {
          // Check if alert already exists
          const exists = existingAlerts.some((a: any) =>
            a.tokenId === alert.tokenId &&
            a.targetPrice === alert.targetPrice &&
            a.condition === alert.condition
          );

          if (!exists) {
            // Generate new ID to avoid conflicts
            mergedAlerts.push({
              ...alert,
              id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            });
          }
        });

        StorageService.set('price_alerts', mergedAlerts);
        console.log(`✅ Price alerts imported: ${data.priceAlerts.length} alerts`);
      }

      console.log('🎉 Import completed successfully!');
      return importedPortfolio;
    } catch (error) {
      console.error('❌ Error importing portfolio:', error);
      return null;
    }
  }
}

export const PortfolioManager = new PortfolioManagerService();
